import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import {
  Autocomplete,
  Box,
  GoogleColorLogo,
  makeStyles,
  Paper,
  Popper,
  PopperProps,
  TextField,
  Typography,
} from 'housecall-ui';

import { AutocompletePrediction, getPlaceDetails, getPredictions } from '../../api/googlePlaces';
import { GooglePlacesObject } from '../../utils/googlePlacesObject';
import { AutoCompleteRenderOption } from '../ContactDetailsStep/AutoCompleteRenderOption';

const useStyles = makeStyles(() => ({
  listbox: {
    padding: 0,
  },
  popper: {
    width: 'fit-content !important',
  },
  container: {
    width: '100%',
  },
}));

type Props = {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (place: GooglePlacesObject) => void;
  error: boolean;
};

const AutoCompletePopper = (props: PopperProps) => {
  const { popper } = useStyles();
  return <Popper {...props} className={classNames(props.className, popper)} placement="bottom-start" />;
};

export const ServiceAreaAddressField = ({ value, onChange, onAddressSelect, error }: Props) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const { listbox, container } = useStyles();

  const fetchPredictions = useCallback(async (newValue: string) => {
    if (!newValue) {
      return;
    }
    const newPredictions = await getPredictions(newValue);
    setPredictions(newPredictions);
  }, []);

  const onQueryChange = async (_: unknown, newValue: string, reason: string) => {
    if (reason === 'input') {
      onChange(newValue);
      fetchPredictions(newValue);
    }
  };

  const onAddressPick = async (_: unknown, place: NonNullable<string | AutocompletePrediction>) => {
    if (!place || typeof place === 'string') {
      return;
    }

    const placeDetails = await getPlaceDetails(place);

    if (!placeDetails) {
      return;
    }

    onChange(placeDetails.formatted_address);
    onAddressSelect(placeDetails);
  };

  return (
    <Autocomplete
      autoComplete={false}
      open={isOpen}
      className={container}
      PaperComponent={({ children, ...props }) => {
        const hasRenderItems = Array.isArray(children) && children.filter(Boolean).length > 0;

        return (
          <Paper {...props} elevation={1}>
            {children}
            {hasRenderItems && (
              <Box ml={2.5} mb={0.5} display="flex" alignItems="center">
                <Box mr={0.5}>
                  <Typography variant="caption">Powered by </Typography>
                </Box>
                <GoogleColorLogo width={50} />
              </Box>
            )}
          </Paper>
        );
      }}
      onOpen={() => setOpen(true)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClose={() => setOpen(false)}
      PopperComponent={AutoCompletePopper}
      freeSolo
      onChange={onAddressPick}
      selectOnFocus
      clearOnBlur
      openOnFocus
      handleHomeEndKeys
      disableClearable
      options={predictions}
      onInputChange={onQueryChange}
      classes={{ listbox }}
      inputValue={value}
      getOptionLabel={(option: AutocompletePrediction) => option.description}
      renderOption={(option: AutocompletePrediction) => <AutoCompleteRenderOption key={option.place_id} {...option} />}
      renderInput={(params) => (
        <TextField
          data-testid="online-booking-service-area-field"
          name="service-area-field"
          {...params}
          variant="filled"
          autoComplete="address-line1"
          fullWidth
          label="Enter your street address"
          error={error}
          helperText={error ? 'Address is required' : ''}
        />
      )}
    />
  );
};
